// Bakery row strip — animated assets above the pump showing what's running.
// Long-press on a row prompts to sell one back; tap shows rate info.
import { TREATS } from '../content/treats.js';
import { state } from '../systems/state.js';
import { synergyMultFor, lastPriceOf, BURNT_CRUST_PAYOUT_MULT } from '../systems/rate.js';
import { fmt } from '../systems/util.js';
import { toast } from './toast.js';
import { subscribe } from '../systems/store.js';
const bakeryEl = document.getElementById('bakery');
const bakeryAssetsById = {};
let onSell = null;
let onPopCrust = null;

export function initBakery({ onSellCb, onPopCrustCb }) {
  onSell = onSellCb;
  onPopCrust = onPopCrustCb;
  // Phase 3: subscribe so refreshBakery() fires on any state mutation
  // (buy, sell, crust attach/pop). Animation-heavy DOM ops kept imperative
  // because preact reconciliation would interfere with the per-row
  // popin/bumped class toggling.
  subscribe(refreshBakery);
}

// Per-baker contribution (rate × owned × stacked synergy mults).
export function bakerContribution(t) {
  const owned = state.owned[t.id] || 0;
  if (!t.rate || owned <= 0) return { contribution: 0, synergy: 1 };
  const synergy = synergyMultFor(t.id);
  return { contribution: t.rate * owned * synergy, synergy };
}

export function bakerInfo(t) {
  const owned = state.owned[t.id] || 0;
  const { contribution, synergy } = bakerContribution(t);
  const synergyTxt = synergy > 1 ? ` ×${synergy} synergy` : '';
  return `${t.name} ×${owned} → +${fmt(contribution)}/s${synergyTxt}`;
}

export function refreshBakery() {
  for (const t of TREATS) {
    if (!t.rate && !t.tapBonus && !t.hatch && t.id !== 'crowncake') continue;
    const owned = state.owned[t.id] || 0;
    const live = !!t.rate || (t.id === 'crowncake' && owned > 0) || (t.hatch && owned > 0);
    let row = bakeryAssetsById[t.id];
    if (owned <= 0) {
      if (row) { row.el.remove(); delete bakeryAssetsById[t.id]; }
      continue;
    }
    if (!row) {
      const el = document.createElement('span');
      el.className = 'asset popin' + (live ? ' live' : '');
      el.dataset.id = t.id;
      if (t.bobMs) el.style.setProperty('--bob', t.bobMs + 'ms');
      el.innerHTML = `<span class="asset-icon">${t.icon}</span><span class="asset-count">×${owned}</span>`;
      // Tap = rate info; long-press (500ms) = confirm sell-back at 50% refund.
      let pressTimer = null, longFired = false;
      el.addEventListener('pointerdown', () => {
        longFired = false;
        pressTimer = setTimeout(() => {
          longFired = true;
          const refund = Math.floor(lastPriceOf(t) * 0.5);
          if (confirm(`Sell 1 ${t.name} for ${fmt(refund)} pumpernickels?`)) onSell(t);
        }, 500);
      });
      const cancelPress = () => { clearTimeout(pressTimer); pressTimer = null; };
      el.addEventListener('pointerup', () => { cancelPress(); if (!longFired) toast(bakerInfo(t)); });
      el.addEventListener('pointercancel', cancelPress);
      el.addEventListener('pointerleave', cancelPress);
      bakeryEl.appendChild(el);
      setTimeout(() => el.classList.remove('popin'), 400);
      row = bakeryAssetsById[t.id] = { el, countEl: el.querySelector('.asset-count'), owned };
    }
    if (row.owned !== owned) {
      row.countEl.textContent = `×${owned}`;
      row.owned = owned;
      row.el.classList.add('bumped');
      setTimeout(() => row.el.classList.remove('bumped'), 220);
    }
    // Burnt crusts attached to this baker — show one popup-able icon per crust.
    const existingCrusts = row.el.querySelectorAll('.burnt-crust');
    existingCrusts.forEach(n => n.remove());
    const crustsHere = (state.burntCrusts || []).map((c, i) => ({ c, i })).filter(x => x.c.bakerId === t.id);
    crustsHere.forEach(({ c, i }, k) => {
      const crustEl = document.createElement('span');
      crustEl.className = 'burnt-crust';
      crustEl.textContent = '🥵';
      crustEl.title = `burnt crust — drained ${fmt(c.drained)} so far. tap to pop for ${fmt(c.drained * BURNT_CRUST_PAYOUT_MULT)}.`;
      crustEl.style.right = (-6 - k * 14) + 'px';
      crustEl.addEventListener('click', (e) => { e.stopPropagation(); onPopCrust(state.burntCrusts.indexOf(c)); });
      row.el.appendChild(crustEl);
    });
    // Always refresh hover tooltip — rate may change due to synergy purchases.
    row.el.title = bakerInfo(t);
  }
}
